import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';

const lbl = { fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 };
const inp = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', outline: 'none', background: 'white', boxSizing: 'border-box' };

const ISP_LIST = ['Spectrum', 'Optimum', 'RCN', 'Verizon Fios', 'Comcast', 'Astound', 'Cox', 'Other'];

const EMPTY_FORM = { isp: '', first_name: '', last_name: '', title: '', email: '', phone: '', notes: '' };

export default function ISPs() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterISP, setFilterISP] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/employee/isp-contacts')
      .then(res => setContacts(res.data.contacts))
      .catch(() => toast.error('Could not load ISP contacts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = contacts.filter(c => {
    const matchSearch = search === '' || `${c.first_name} ${c.last_name} ${c.email} ${c.isp}`.toLowerCase().includes(search.toLowerCase());
    const matchISP = filterISP === '' || c.isp === filterISP;
    return matchSearch && matchISP;
  });

  // Group by ISP
  const grouped = filtered.reduce((acc, c) => {
    const key = c.isp || 'Other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  const handleEdit = (c) => {
    setForm({ isp: c.isp || '', first_name: c.first_name, last_name: c.last_name, title: c.title || '', email: c.email || '', phone: c.phone || '', notes: c.notes || '' });
    setEditingId(c.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNew = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.first_name || !form.last_name || !form.isp) {
      toast.error('First name, last name, and ISP are required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.patch('/employee/isp-contacts/' + editingId, form);
        toast.success('Contact updated!');
      } else {
        await api.post('/employee/isp-contacts', form);
        toast.success('Contact added!');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save contact');
    } finally { setSaving(false); }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this contact? They will no longer appear in dropdowns.')) return;
    try {
      await api.patch('/employee/isp-contacts/' + id, { status: 'inactive' });
      toast.success('Contact deactivated');
      load();
    } catch (err) {
      toast.error('Could not deactivate contact');
    }
  };

  const ispOptions = [...new Set(contacts.map(c => c.isp).filter(Boolean))];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <EmployeeSidebar />
      <div style={{ flex: 1, padding: '2rem', maxWidth: 1000 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div>
            <h1 style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: 4 }}>ISP Contacts</h1>
            <p style={{ color: '#64748b', fontSize: 14 }}>All ISP account reps across providers — only visible to Super Admin.</p>
          </div>
          <button onClick={handleNew} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            + Add Contact
          </button>
        </div>

        {/* Add/Edit form */}
        {showForm && (
          <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', padding: '1.5rem', marginBottom: 24, marginTop: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
              {editingId ? '✏️ Edit Contact' : '➕ New ISP Contact'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={lbl}>ISP *</label>
                <select value={form.isp} onChange={e => setForm(p => ({ ...p, isp: e.target.value }))} style={inp}>
                  <option value="">Select ISP</option>
                  {ISP_LIST.map(i => <option key={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Title</label>
                <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} style={inp} placeholder="e.g. Senior Account Manager" />
              </div>
              <div>
                <label style={lbl}>First Name *</label>
                <input value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} style={inp} placeholder="First name" />
              </div>
              <div>
                <label style={lbl}>Last Name *</label>
                <input value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} style={inp} placeholder="Last name" />
              </div>
              <div>
                <label style={lbl}>Email</label>
                <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} style={inp} placeholder="rep@isp.com" />
              </div>
              <div>
                <label style={lbl}>Phone</label>
                <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} style={inp} placeholder="555-000-0000" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Notes</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} style={{ ...inp, minHeight: 70, resize: 'vertical' }} placeholder="Any notes about this contact..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '9px 20px', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : editingId ? 'Update Contact' : 'Save Contact'}
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, marginTop: showForm ? 0 : 16 }}>
          <input style={{ width: 280, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14 }}
            placeholder="Search contacts..."
            value={search} onChange={e => setSearch(e.target.value)} />
          <select value={filterISP} onChange={e => setFilterISP(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, background: 'white' }}>
            <option value="">All ISPs</option>
            {ispOptions.map(i => <option key={i}>{i}</option>)}
          </select>
          <div style={{ marginLeft: 'auto', padding: '8px 0', fontSize: 13, color: '#64748b' }}>
            {filtered.length} contact{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ background: 'white', borderRadius: 12, padding: '3rem', textAlign: 'center', color: '#94a3b8', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🔌</div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>No ISP contacts yet</div>
            <div style={{ fontSize: 13 }}>Add contacts using the button above, or they'll appear here when added from a property contract.</div>
          </div>
        ) : (
          Object.entries(grouped).map(([isp, ispContacts]) => (
            <div key={isp} style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', marginBottom: 20, overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>🔌</span>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{isp}</span>
                <span style={{ background: '#e2e8f0', color: '#64748b', borderRadius: 100, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
                  {ispContacts.length} contact{ispContacts.length !== 1 ? 's' : ''}
                </span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#fafafa' }}>
                    {['Name', 'Title', 'Email', 'Phone', 'Notes', ''].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ispContacts.map((c, i) => (
                    <tr key={c.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600 }}>{c.first_name} {c.last_name}</div>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b' }}>{c.title || '—'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        {c.email ? <a href={'mailto:' + c.email} style={{ color: '#2563eb', textDecoration: 'none' }}>{c.email}</a> : '—'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#475569' }}>{c.phone || '—'}</td>
                      <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 12, maxWidth: 200 }}>{c.notes || '—'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => handleEdit(c)} style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                          <button onClick={() => handleDeactivate(c.id)} style={{ background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Remove</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
        <LegalFooter />
      </div>
    </div>
  );
}
